import {
  buildMapPreferencesCompositeKey,
  createMapPreferencesRecord,
  parseMapPreferencesRecord
} from "../helpers/mapPreferences.helpers";
import {
  MapPreferencesStore,
  MapViewPreferences
} from "../types/mapPreferences.types";

export class MapPreferencesCache {
  static readonly CACHE_KEY = "map_preferences_v1";

  /** Saves map preferences before localStorage.clear(); call the returned fn after clear to restore. */
  static preserveOnLogout(): () => void {
    const saved = localStorage.getItem(this.CACHE_KEY);
    return () => {
      if (saved !== null) {
        localStorage.setItem(this.CACHE_KEY, saved);
      }
    };
  }

  static get(userId: string, accountId: string): MapViewPreferences | null {
    const store = this.readStore();
    const compositeKey = buildMapPreferencesCompositeKey(userId, accountId);
    const record = store[compositeKey];

    if (!record) return null;

    return parseMapPreferencesRecord(record, userId, accountId);
  }

  static save(
    userId: string,
    accountId: string,
    preferences: MapViewPreferences
  ): void {
    const store = this.readStore();
    const compositeKey = buildMapPreferencesCompositeKey(userId, accountId);

    store[compositeKey] = createMapPreferencesRecord(
      userId,
      accountId,
      preferences
    );

    this.writeStore(store);
  }

  static mergeAndSave(
    userId: string,
    accountId: string,
    partial: Partial<MapViewPreferences>
  ): void {
    const existing =
      this.get(userId, accountId) ??
      ({
        showSatelliteMode: false,
        showClusters: true,
        showTraffic: false,
        showGeozones: true,
        showInactiveObjects: false,
        autoFitVisibleUnits: false,
        filters: { groups: [], objectTypes: [], keyId: [] }
      } satisfies MapViewPreferences);

    this.save(userId, accountId, {
      ...existing,
      ...partial,
      filters: partial.filters ?? existing.filters
    });
  }

  static remove(userId: string, accountId: string): void {
    const store = this.readStore();
    const compositeKey = buildMapPreferencesCompositeKey(userId, accountId);

    if (store[compositeKey]) {
      delete store[compositeKey];
      this.writeStore(store);
    }
  }

  static clear(): void {
    localStorage.removeItem(this.CACHE_KEY);
  }

  private static readStore(): MapPreferencesStore {
    const cached = localStorage.getItem(this.CACHE_KEY);
    if (!cached) return {};

    try {
      const parsed = JSON.parse(cached) as MapPreferencesStore;
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }

  private static writeStore(store: MapPreferencesStore): void {
    localStorage.setItem(this.CACHE_KEY, JSON.stringify(store));
  }
}
